"use client";

// @ubm/product-ui/blocks/week-calendar — HOUSE IMPLEMENTATION from the public UIArc specification
// (https://uiarc.dev/components/blocks/week-calendar/markdown) ; original Pro implementation not received.
// Géométrie : le noyau L0 @ubm/ui/components/time-grid, PARTAGÉ avec l'agenda Booking (sélection, appui long, glisser
// d'évènements, étirement, aimantation 15 min, ligne « maintenant ») — aucune copie de ces contrôleurs ici. Ce bloc adapte
// autour : dates civiles dans un fuseau IANA (calcul une fois par changement d'évènements), locale, rangée journée
// entière, vues, clavier, popovers, annulation. Composition : Popover, SegmentedControl, Calendar, Input, Select,
// Button, Avatar, menu déroulant canons.
// Adaptations maison déclarées : sans évènements, le calendrier est VIDE (pas de semaine d'exemple). `onCreate`,
// `onUpdate` et `onDelete` sont des ports Promise : la proposition s'affiche en attente, n'est validée qu'après réussite,
// un refus la retire et garde le formulaire ; sans port, l'action correspondante est indisponible. L'annulation (Cmd/Ctrl+Z,
// toast) passe par les mêmes ports. La seule horloge est la ligne « maintenant », à la minute.
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type FocusEvent, type KeyboardEvent, type MouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { AnimatePresence, LayoutGroup, motion, useReducedMotionConfig as useReducedMotion } from "motion/react";
import { Check, ChevronLeft, ChevronRight, MapPin, Plus, SlidersHorizontal } from "lucide-react";
import { Select } from "@ubm/ui/components/select";
import { Avatar } from "@ubm/ui/components/avatar";
import { Button } from "@ubm/ui/components/button";
import { Calendar } from "@ubm/ui/components/calendar";
import { Checkbox } from "@ubm/ui/components/checkbox";
import { DropdownMenuCheckboxItem, DropdownMenuContentBase, DropdownMenuItemIndicator, DropdownMenuPortal, DropdownMenuRoot, DropdownMenuTrigger, dropdownMenuClassNames } from "@ubm/ui/components/dropdown-menu";
import { Input } from "@ubm/ui/components/input";
import { Popover, PopoverAnchor, PopoverContent } from "@ubm/ui/components/popover";
import SegmentedControl from "@ubm/ui/components/segmented-control";
import { TimeGrid, TimeGridEvent, timeGridPercent, type TimeGridColumn, type TimeGridEventIntent, type TimeGridRange } from "@ubm/ui/components/time-grid";
import { motionTokens } from "@ubm/ui/motion-tokens";
import { LiveStatus, PresenceDiv, useContainerWidth } from "../_conversation";
import { placeOverlaps } from "./layout";
import { addDaysKey, dayKey, diffDays, keyAsDate, keyAsLocalDate, localDateKey, pad, parseKey, weekdayOf, zonedInstant, zonedParts } from "./zoned";
import styles from "./week-calendar.module.css";

export type CalendarView = "week" | "workweek" | "day" | "3day" | "agenda";
export interface CalendarPerson { name: string; avatar?: string }
export interface CalendarEvent {
  id: string;
  title: string;
  startDate: Date;
  endDate: Date;
  calendarId: string;
  /** All-day events end at the start of the day after their last day. */
  allDay?: boolean;
  location?: string;
  attendees?: CalendarPerson[];
}
export interface CalendarSource {
  id: string;
  name: string;
  /** blue, green, violet, amber, rose, teal, or a CSS colour token. */
  color: string;
  /** Starts toggled off. */
  hidden?: boolean;
}
export interface WeekCalendarStrings {
  today?: string;
  previous?: string;
  next?: string;
  views?: string;
  viewNames?: Partial<Record<CalendarView, string>>;
  calendars?: string;
  newEvent?: string;
  allDay?: string;
  title?: string;
  untitled?: string;
  date?: string;
  start?: string;
  end?: string;
  calendar?: string;
  location?: string;
  save?: string;
  cancel?: string;
  delete?: string;
  undo?: string;
  noEvents?: string;
  more?: (n: number) => string;
  createUnavailable?: string;
  updateUnavailable?: string;
  deleteUnavailable?: string;
  failed?: string;
  invalidRange?: string;
  instructions?: string;
  gridLabel?: (range: string) => string;
  cellLabel?: (date: string, count: number) => string;
  created?: (title: string) => string;
  moved?: (title: string, when: string) => string;
  updated?: (title: string) => string;
  deleted?: (title: string) => string;
  restored?: (title: string) => string;
  undone?: string;
  /** Announced when a command targets an event whose previous change is still saving. */
  stillSaving?: (title: string) => string;
  rangeChanged?: (range: string) => string;
}
/** What `renderEvent` receives for one timed event block (a multi-day event renders once per day it covers). */
export interface CalendarEventRenderContext {
  event: CalendarEvent;
  calendar?: CalendarSource;
  /** The civil day (YYYY-MM-DD, in the grid's zone) this block sits on. */
  day: string;
  /** Formatted start and end in the grid's zone and locale. */
  startLabel: string;
  endLabel: string;
  /** A change to this event is waiting for its port. */
  pending: boolean;
  /** The event's details are open. */
  selected: boolean;
  /** The block is the first / last day of a multi-day event. */
  first: boolean;
  last: boolean;
}
export interface WeekCalendarProps {
  events?: CalendarEvent[];
  defaultEvents?: CalendarEvent[];
  /** The full list after every committed change (create, move, resize, edit, delete, undo). */
  onEventsChange?: (events: CalendarEvent[]) => void;
  /** Saves a new event (also restores a deleted one on undo). May return the stored event. Without it, creating is unavailable. */
  onCreate?: (event: CalendarEvent) => void | CalendarEvent | Promise<void | CalendarEvent>;
  /** Saves a move, resize or edit, with the event before the change. Without it, events are read-only. */
  onUpdate?: (event: CalendarEvent, previous: CalendarEvent) => void | Promise<void>;
  /** Deletes an event. Without it, deleting is unavailable. */
  onDelete?: (event: CalendarEvent) => void | Promise<void>;
  calendars?: CalendarSource[];
  defaultDate?: Date;
  view?: CalendarView;
  defaultView?: CalendarView;
  onViewChange?: (view: CalendarView) => void;
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  startHour?: number;
  endHour?: number;
  /** IANA zone the grid runs in. Defaults to the browser's zone. */
  timeZone?: string;
  locale?: string;
  /** Injected clock (tests, server-rendered previews). Without it the now line follows the minute. */
  now?: Date;
  /** Id for a new event. Defaults to a random id. */
  newId?: () => string;
  /**
   * Content of a timed event block. The grid keeps the block itself (position, accent, accessible label, click to open,
   * drag, resize, keyboard, pending and read-only states); this only replaces what is drawn inside. Without it, the
   * default title, time, place and attendees are drawn. Keep the content non-interactive: the block is the button.
   */
  renderEvent?: (context: CalendarEventRenderContext) => ReactNode;
  strings?: WeekCalendarStrings;
  className?: string;
  style?: CSSProperties;
}

const VIEW_NAMES: Record<CalendarView, string> = { week: "Week", workweek: "Work week", day: "Day", "3day": "3 days", agenda: "Agenda" };
const STRINGS: Required<Omit<WeekCalendarStrings, "viewNames">> = {
  today: "Today",
  previous: "Previous",
  next: "Next",
  views: "View",
  calendars: "Calendars",
  newEvent: "New event",
  allDay: "All day",
  title: "Title",
  untitled: "New event",
  date: "Date",
  start: "Starts",
  end: "Ends",
  calendar: "Calendar",
  location: "Location",
  save: "Save",
  cancel: "Cancel",
  delete: "Delete",
  undo: "Undo",
  noEvents: "No events",
  more: n => `+${n} more`,
  createUnavailable: "Creating events isn't available here.",
  updateUnavailable: "Editing events isn't available here.",
  deleteUnavailable: "Deleting isn't available here.",
  failed: "That didn't go through. Nothing changed.",
  invalidRange: "The end must be after the start.",
  instructions: "Arrow keys move between days. N drafts an event, Enter opens, Delete removes, Alt with arrows moves the focused event, Shift+Alt with up and down changes its length, T goes to today, D and W switch views, Cmd or Ctrl+Z undoes.",
  gridLabel: range => `Calendar, ${range}`,
  cellLabel: (date, count) => `${date}, ${count === 0 ? "no events" : count === 1 ? "1 event" : `${count} events`}`,
  created: title => `${title} created.`,
  moved: (title, when) => `${title} moved to ${when}.`,
  updated: title => `${title} updated.`,
  deleted: title => `${title} deleted.`,
  restored: title => `${title} restored.`,
  undone: "Change undone.",
  stillSaving: title => `${title} is still saving. Try again when it's done.`,
  rangeChanged: range => range,
};
const COLORS: Record<string, string> = { blue: "var(--entity-8)", green: "var(--entity-2)", violet: "var(--entity-3)", amber: "var(--entity-1)", rose: "var(--entity-7)", teal: "var(--entity-6)" };
const colorOf = (c?: string) => (c ? COLORS[c] ?? c : "var(--arc-accent)");
const SNAP = 15, HOUR_PX = 52, NARROW = 640, COMPACT = 900, SIDEBAR = 1100, ALLDAY_ROWS = 3;
const SPAN: Record<CalendarView, number> = { week: 7, workweek: 7, day: 1, "3day": 3, agenda: 14 };

let seq = 0;
const randomId = () => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `evt-${Date.now().toString(36)}-${++seq}`);

interface Segment { ev: CalendarEvent; day: string; start: number; end: number; first: boolean; last: boolean }
type Undo = { kind: "update"; before: CalendarEvent; after: CalendarEvent } | { kind: "delete"; event: CalendarEvent } | { kind: "create"; event: CalendarEvent };
interface Popup { kind: "create" | "details"; id?: string; day: string; start: number; end: number; rect: { top: number; left: number; width: number; height: number } }

function useMinuteClock(fixed?: Date) {
  const [tick, setTick] = useState(() => fixed ?? new Date());
  useEffect(() => {
    if (fixed) return;
    let timer: ReturnType<typeof setTimeout>;
    const arm = () => { timer = setTimeout(() => { setTick(new Date()); arm(); }, 60000 - (Date.now() % 60000) + 20); };
    arm();
    return () => clearTimeout(timer);
  }, [fixed]);
  return fixed ?? tick;
}

export function WeekCalendar({ events: eventsProp, defaultEvents = [], onEventsChange, onCreate, onUpdate, onDelete, calendars = [], defaultDate, view: viewProp, defaultView = "week", onViewChange, weekStartsOn = 1, startHour = 0, endHour = 24, timeZone, locale = "en-US", now: nowProp, newId = randomId, renderEvent: renderEventContent, strings, className, style }: WeekCalendarProps) {
  const t = { ...STRINGS, ...strings };
  const viewNames = { ...VIEW_NAMES, ...strings?.viewNames };
  const reduced = !!useReducedMotion();
  const uid = useId();
  const root = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const width = useContainerWidth(root, 1200);
  const narrow = width > 0 && width < NARROW;
  const compact = width > 0 && width >= NARROW && width < COMPACT;
  const withSidebar = width === 0 || width >= SIDEBAR;
  const now = useMinuteClock(nowProp);
  const nowParts = zonedParts(now, timeZone);
  const todayKey = dayKey(nowParts);

  const [inner, setInner] = useState(defaultEvents);
  const committed = eventsProp ?? inner;
  /** The list as of the last render: read after an await, so a settled port never commits a stale copy. */
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const latest = useRef(committed);
  latest.current = committed;
  const [proposals, setProposals] = useState<Map<string, CalendarEvent | "delete">>(new Map());
  const [anchor, setAnchor] = useState(() => (defaultDate ? dayKey(zonedParts(defaultDate, timeZone)) : todayKey));
  const [ownView, setOwnView] = useState<CalendarView>(defaultView);
  const requested = viewProp ?? ownView;
  const view: CalendarView = narrow && (requested === "week" || requested === "workweek") ? "3day" : !narrow && requested === "3day" ? "week" : requested;
  const [hidden, setHidden] = useState(() => new Set(calendars.filter(c => c.hidden).map(c => c.id)));
  const [focusedDay, setFocusedDay] = useState(anchor);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [popup, setPopup] = useState<Popup | null>(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftCalendar, setDraftCalendar] = useState(calendars.find(c => !c.hidden)?.id ?? calendars[0]?.id ?? "");
  const [form, setForm] = useState({ title: "", date: "", start: "", end: "", calendarId: "", location: "" });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const [toast, setToast] = useState<{ message: string; undo?: Undo } | null>(null);
  const [undoStack, setUndoStack] = useState<Undo[]>([]);
  const [direction, setDirection] = useState(1);
  /** Bumped by a navigation the person makes (prev/next, today, day keys, mini month, view): only that slides the range. A
   *  container resize that changes the effective view re-renders in place, without replaying the slide. */
  const [navKey, setNavKey] = useState(0);
  const navigated = () => setNavKey(k => k + 1);
  const [allDayOpen, setAllDayOpen] = useState(false);
  const [miniMonth, setMiniMonth] = useState(() => keyAsLocalDate(anchor));
  useEffect(() => { setMiniMonth(keyAsLocalDate(anchor)); }, [anchor]);
  const focusDayAfter = useRef<string | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const swipe = useRef<{ x: number; y: number; t: number } | null>(null);

  // ---- range ----
  const range = useMemo(() => {
    const wd = weekdayOf(anchor);
    if (view === "week") { const start = addDaysKey(anchor, -((wd - weekStartsOn + 7) % 7)); return Array.from({ length: 7 }, (_, i) => addDaysKey(start, i)); }
    if (view === "workweek") { const start = addDaysKey(anchor, -((wd - 1 + 7) % 7)); return Array.from({ length: 5 }, (_, i) => addDaysKey(start, i)); }
    if (view === "day") return [anchor];
    if (view === "3day") return [anchor, addDaysKey(anchor, 1), addDaysKey(anchor, 2)];
    return Array.from({ length: 14 }, (_, i) => addDaysKey(anchor, i));
  }, [anchor, view, weekStartsOn]);
  const first = range[0]!, last = range[range.length - 1]!;
  const dateFmt = useMemo(() => new Intl.DateTimeFormat(locale, { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" }), [locale]);
  const shortFmt = useMemo(() => new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }), [locale]);
  const rangeFmt = useMemo(() => new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }), [locale]);
  const timeFmt = useMemo(() => new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit", timeZone }), [locale, timeZone]);
  const hourFmt = useMemo(() => new Intl.DateTimeFormat(locale, { hour: "numeric", timeZone: "UTC" }), [locale]);
  const rangeLabel = first === last ? rangeFmt.format(keyAsDate(first)) : (rangeFmt as Intl.DateTimeFormat & { formatRange?: (a: Date, b: Date) => string }).formatRange?.(keyAsDate(first), keyAsDate(last)) ?? `${rangeFmt.format(keyAsDate(first))} – ${rangeFmt.format(keyAsDate(last))}`;

  // ---- events: committed + proposals, zoned once per change ----
  const shown = useMemo(() => {
    const list = committed.map(e => { const p = proposals.get(e.id); return p && p !== "delete" ? p : e; });
    for (const [id, p] of proposals) if (p !== "delete" && !committed.some(e => e.id === id)) list.push(p);
    return list.filter(e => !hidden.has(e.calendarId));
  }, [committed, proposals, hidden]);
  const zoned = useMemo(() => shown.map(ev => {
    const s = zonedParts(ev.startDate, timeZone);
    const endInstant = new Date(Math.max(ev.endDate.getTime(), ev.startDate.getTime()));
    const e = zonedParts(endInstant, timeZone);
    let endKey = dayKey(e);
    let endMin = e.hour * 60 + e.minute;
    if (endMin === 0 && endKey !== dayKey(s)) { endKey = addDaysKey(endKey, -1); endMin = 1440; }
    return { ev, startKey: dayKey(s), startMin: s.hour * 60 + s.minute, endKey, endMin };
  }), [shown, timeZone]);
  const calendarName = (id: string) => calendars.find(c => c.id === id)?.name ?? "";
  const calendarColor = (id: string) => colorOf(calendars.find(c => c.id === id)?.color);
  const windowStart = startHour * 60, windowEnd = endHour * 60, windowMinutes = windowEnd - windowStart;

  const segments = useMemo(() => {
    const out = new Map<string, Segment[]>();
    for (const z of zoned) {
      if (z.ev.allDay) continue;
      for (let key = diffDays(first, z.startKey) >= 0 ? z.startKey : first; diffDays(key, z.endKey) >= 0 && diffDays(key, last) >= 0; key = addDaysKey(key, 1)) {
        const start = key === z.startKey ? z.startMin : 0;
        const end = key === z.endKey ? z.endMin : 1440;
        if (end <= windowStart || start >= windowEnd) continue;
        const list = out.get(key) ?? [];
        list.push({ ev: z.ev, day: key, start: Math.max(start, windowStart) - windowStart, end: Math.min(end, windowEnd) - windowStart, first: key === z.startKey, last: key === z.endKey });
        out.set(key, list);
      }
    }
    return out;
  }, [zoned, first, last, windowStart, windowEnd]);
  const allDay = useMemo(() => zoned.filter(z => z.ev.allDay && diffDays(z.startKey, last) >= 0 && diffDays(first, z.endKey) >= 0).sort((a, b) => a.startKey.localeCompare(b.startKey) || diffDays(b.startKey, b.endKey) - diffDays(a.startKey, a.endKey)), [zoned, first, last]);
  const ordered = useMemo(() => [...zoned].filter(z => diffDays(z.startKey, last) >= 0 && diffDays(first, z.endKey) >= 0).sort((a, b) => a.ev.startDate.getTime() - b.ev.startDate.getTime()).map(z => z.ev), [zoned, first, last]);

  // ---- commit helpers ----
  const commit = useCallback((next: CalendarEvent[]) => { latest.current = next; if (eventsProp === undefined) setInner(next); onEventsChange?.(next); }, [eventsProp, onEventsChange]);
  const propose = (id: string, value: CalendarEvent | "delete" | null) => setProposals(cur => { const next = new Map(cur); if (value === null) next.delete(id); else next.set(id, value); return next; });
  const whenLabel = (ev: CalendarEvent) => ev.allDay ? `${dateFmt.format(keyAsDate(dayKey(zonedParts(ev.startDate, timeZone))))}` : `${dateFmt.format(keyAsDate(dayKey(zonedParts(ev.startDate, timeZone))))}, ${timeFmt.format(ev.startDate)}`;
  const eventLabel = (ev: CalendarEvent) => [ev.title, ev.allDay ? `${whenLabel(ev)}, ${t.allDay}` : `${whenLabel(ev)} à ${timeFmt.format(ev.endDate)}`, calendarName(ev.calendarId), ev.location].filter(Boolean).join(", ");

  /**
   * One port call per event at a time. Held synchronously from the call to its settlement, so a second command fired before
   * React re-renders (two Alt+Arrow presses, a delete during a move, an undo on a saving event) is refused, not queued.
   */
  const saving = useRef(new Set<string>());
  const claim = (id: string, title: string) => {
    if (saving.current.has(id)) { setStatus(t.stillSaving(title)); return false; }
    saving.current.add(id);
    return true;
  };
  const release = (id: string) => { saving.current.delete(id); };
  const runUpdate = async (next: CalendarEvent, previous: CalendarEvent, announce: string, record = true) => {
    if (!onUpdate) { setStatus(t.updateUnavailable); return false; }
    if (!claim(next.id, previous.title)) return false;
    propose(next.id, next);
    try { await onUpdate(next, previous); }
    catch { if (!alive.current) return false; propose(next.id, null); release(next.id); setStatus(t.failed); setToast({ message: t.failed }); return false; }
    if (!alive.current) return false;
    release(next.id);
    commit(latest.current.map(e => (e.id === next.id ? next : e)));
    propose(next.id, null);
    if (record) { setUndoStack(s => [...s, { kind: "update", before: previous, after: next }]); setToast({ message: announce, undo: { kind: "update", before: previous, after: next } }); }
    setStatus(announce);
    return true;
  };
  const runCreate = async (draft: CalendarEvent, announce: string, record = true) => {
    if (!onCreate) { setStatus(t.createUnavailable); return false; }
    if (!claim(draft.id, draft.title)) return false;
    propose(draft.id, draft);
    let saved: CalendarEvent = draft;
    try { const result = await onCreate(draft); if (result && typeof result === "object") saved = result; }
    catch { if (!alive.current) return false; propose(draft.id, null); release(draft.id); setStatus(t.failed); return false; }
    if (!alive.current) return false;
    release(draft.id);
    commit([...latest.current.filter(e => e.id !== saved.id), saved]);
    propose(draft.id, null);
    if (record) setUndoStack(s => [...s, { kind: "create", event: saved }]);
    setStatus(announce);
    return true;
  };
  const runDelete = async (ev: CalendarEvent, record = true) => {
    if (!onDelete) { setStatus(t.deleteUnavailable); return false; }
    if (!claim(ev.id, ev.title)) return false;
    propose(ev.id, "delete");
    try { await onDelete(ev); }
    catch { if (!alive.current) return false; propose(ev.id, null); release(ev.id); setStatus(t.failed); setToast({ message: t.failed }); return false; }
    if (!alive.current) return false;
    release(ev.id);
    commit(latest.current.filter(e => e.id !== ev.id));
    propose(ev.id, null);
    if (record) { setUndoStack(s => [...s, { kind: "delete", event: ev }]); setToast({ message: t.deleted(ev.title), undo: { kind: "delete", event: ev } }); }
    setStatus(t.deleted(ev.title));
    if (selectedId === ev.id) setSelectedId(null);
    return true;
  };
  const undo = async (entry?: Undo) => {
    const target = entry ?? undoStack[undoStack.length - 1];
    if (!target) return;
    const targetEvent = target.kind === "update" ? target.after : target.event;
    // An undo on an event that is still saving is refused and stays available.
    if (saving.current.has(targetEvent.id)) { setStatus(t.stillSaving(targetEvent.title)); return; }
    setUndoStack(s => s.filter(x => x !== target));
    setToast(null);
    let ok: boolean;
    if (target.kind === "update") ok = await runUpdate(target.before, target.after, t.undone, false);
    else if (target.kind === "delete") ok = await runCreate(target.event, t.restored(target.event.title), false);
    else ok = await runDelete(target.event, false);
    if (ok && target.kind !== "delete") setStatus(t.undone);
  };

  // ---- geometry → instants ----
  const instantAt = (key: string, minuteOfDay: number) => { const { year, month, day } = parseKey(key); return zonedInstant(year, month, day, Math.floor(minuteOfDay / 60), minuteOfDay % 60, timeZone); };
  const moveTo = (ev: CalendarEvent, key: string, startMinuteOfDay: number, endMinuteOfDay: number): CalendarEvent => ({ ...ev, startDate: instantAt(key, startMinuteOfDay), endDate: endMinuteOfDay >= 1440 ? instantAt(addDaysKey(key, 1), endMinuteOfDay - 1440) : instantAt(key, endMinuteOfDay) });
  const onEventChange = (intent: TimeGridEventIntent) => {
    const ev = committed.find(e => e.id === intent.id);
    if (!ev) return;
    const next = moveTo(ev, intent.columnKey, intent.startMinute + windowStart, intent.endMinute + windowStart);
    void runUpdate(next, ev, intent.kind === "move" ? t.moved(ev.title, whenLabel(next)) : t.updated(ev.title));
  };

  // ---- popovers ----
  const rectFor = (key: string, start: number, end: number, id?: string) => {
    const box = root.current?.getBoundingClientRect();
    const target = id ? root.current?.querySelector<HTMLElement>(`[data-tg-id="${CSS.escape(id)}"]`) : null;
    const col = root.current?.querySelector<HTMLElement>(`[data-day="${key}"]`);
    const r = target?.getBoundingClientRect() ?? col?.getBoundingClientRect();
    if (!box || !r) return { top: 0, left: 0, width: 0, height: 0 };
    if (target || !col) return { top: r.top - box.top, left: r.left - box.left, width: r.width, height: r.height };
    const top = r.top - box.top + ((start - windowStart) / windowMinutes) * r.height;
    return { top, left: r.left - box.left, width: r.width, height: Math.max(((end - start) / windowMinutes) * r.height, 8) };
  };
  const openCreate = (key: string, start: number, end: number, origin?: HTMLElement) => {
    returnFocus.current = origin ?? (document.activeElement as HTMLElement | null);
    setDraftTitle("");
    setFormError(null);
    setPopup({ kind: "create", day: key, start, end, rect: rectFor(key, start, end) });
  };
  const openDetails = (ev: CalendarEvent, origin?: HTMLElement | null) => {
    const z = zoned.find(x => x.ev.id === ev.id);
    if (!z) return;
    returnFocus.current = origin ?? (document.activeElement as HTMLElement | null);
    const s = zonedParts(ev.startDate, timeZone), e = zonedParts(ev.endDate, timeZone);
    setForm({ title: ev.title, date: dayKey(s), start: `${pad(s.hour)}:${pad(s.minute)}`, end: `${pad(e.hour)}:${pad(e.minute)}`, calendarId: ev.calendarId, location: ev.location ?? "" });
    setFormError(null);
    setSelectedId(ev.id);
    setPopup({ kind: "details", id: ev.id, day: z.startKey, start: z.startMin, end: z.endMin, rect: rectFor(z.startKey, z.startMin, z.endMin, ev.id) });
  };
  const closePopup = () => {
    setPopup(null);
    setBusy(false);
    const back = returnFocus.current;
    returnFocus.current = null;
    if (back && back.isConnected) requestAnimationFrame(() => { if (alive.current && back.isConnected) back.focus({ preventScroll: true }); });
  };
  const saveDraft = async () => {
    if (!popup || popup.kind !== "create" || busy) return;
    const draft: CalendarEvent = { id: newId(), title: draftTitle.trim() || t.untitled, startDate: instantAt(popup.day, popup.start), endDate: popup.end >= 1440 ? instantAt(addDaysKey(popup.day, 1), popup.end - 1440) : instantAt(popup.day, popup.end), calendarId: draftCalendar };
    setBusy(true);
    setFormError(null);
    const ok = await runCreate(draft, t.created(draft.title));
    if (!alive.current) return;
    setBusy(false);
    if (ok) closePopup(); else setFormError(onCreate ? t.failed : t.createUnavailable);
  };
  const saveDetails = async () => {
    const ev = committed.find(e => e.id === popup?.id);
    if (!ev || busy) return;
    const fields = { title: form.title.trim() || ev.title, calendarId: form.calendarId || ev.calendarId, location: form.location.trim() || undefined };
    let next: CalendarEvent;
    if (ev.allDay) {
      // All-day: only title, calendar and place are edited here; the civil range (exclusive end) and the flag are kept.
      next = { ...ev, ...fields };
    } else {
      const minutes = (value: string) => { const [h, m] = value.split(":").map(Number); return (h ?? 0) * 60 + (m ?? 0); };
      const z = zoned.find(x => x.ev.id === ev.id);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date) || !z) { setFormError(t.invalidRange); return; }
      const start = minutes(form.start);
      let end = minutes(form.end);
      const startDate = instantAt(form.date, start);
      let endDate: Date;
      if (z.endKey === z.startKey) {
        // One day: the end time on the same date (an end at midnight reads 00:00 and means the end of that day).
        if (end === 0 && z.endMin === 1440) end = 1440;
        if (!(end > start)) { setFormError(t.invalidRange); return; }
        endDate = end >= 1440 ? instantAt(addDaysKey(form.date, 1), end - 1440) : instantAt(form.date, end);
      } else {
        // Several days: the end keeps its own date, shifted with the start, plus any change to its time.
        const e = zonedParts(ev.endDate, timeZone);
        endDate = new Date(ev.endDate.getTime() + (startDate.getTime() - ev.startDate.getTime()) + (end - (e.hour * 60 + e.minute)) * 60000);
        if (!(endDate.getTime() > startDate.getTime())) { setFormError(t.invalidRange); return; }
      }
      next = { ...ev, ...fields, startDate, endDate };
    }
    setBusy(true);
    setFormError(null);
    const ok = await runUpdate(next, ev, t.updated(next.title));
    if (!alive.current) return;
    setBusy(false);
    if (ok) closePopup(); else setFormError(onUpdate ? t.failed : t.updateUnavailable);
  };
  const deleteFromDetails = async () => {
    const ev = committed.find(e => e.id === popup?.id);
    if (!ev || busy) return;
    setBusy(true);
    const ok = await runDelete(ev);
    if (!alive.current) return;
    setBusy(false);
    if (ok) { returnFocus.current = null; closePopup(); } else setFormError(onDelete ? t.failed : t.deleteUnavailable);
  };

  // ---- navigation ----
  const setView = (next: CalendarView) => { navigated(); if (viewProp === undefined) setOwnView(next); onViewChange?.(next); };
  const shift = (steps: number) => {
    setDirection(steps > 0 ? 1 : -1);
    navigated();
    const span = SPAN[view];
    setAnchor(a => addDaysKey(a, steps * span));
    setFocusedDay(d => addDaysKey(d, steps * span));
  };
  const goToday = () => { setDirection(diffDays(anchor, todayKey) >= 0 ? 1 : -1); navigated(); setAnchor(todayKey); setFocusedDay(todayKey); focusDayAfter.current = todayKey; };
  useEffect(() => { setStatus(t.rangeChanged(rangeLabel)); }, [rangeLabel]);
  useEffect(() => {
    const key = focusDayAfter.current;
    if (!key) return;
    focusDayAfter.current = null;
    root.current?.querySelector<HTMLElement>(`[data-day="${key}"]`)?.focus();
  });
  const moveFocusDay = (delta: number) => {
    const next = addDaysKey(focusedDay, delta);
    if (diffDays(first, next) < 0 || diffDays(next, last) < 0) { setDirection(delta > 0 ? 1 : -1); navigated(); setAnchor(a => addDaysKey(a, delta > 0 ? SPAN[view] : -SPAN[view])); }
    setFocusedDay(next);
    focusDayAfter.current = next;
  };
  const nextFreeHour = (key: string) => {
    const busyRanges = (segments.get(key) ?? []).map(s => [s.start + windowStart, s.end + windowStart] as const);
    const from = key === todayKey ? Math.max(windowStart, (nowParts.hour + 1) * 60) : Math.max(windowStart, 9 * 60);
    for (let m = from; m + 60 <= windowEnd; m += 60) if (!busyRanges.some(([a, b]) => a < m + 60 && b > m)) return m;
    return Math.max(windowStart, Math.min(from, windowEnd - 60));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) || target.closest('[role="dialog"],[role="menu"],[role="listbox"],[role="radiogroup"]')) return;
    const eventEl = target.closest<HTMLElement>("[data-tg-id]");
    const ev = eventEl ? committed.find(e => e.id === eventEl.dataset.tgId) : undefined;
    const mod = event.metaKey || event.ctrlKey;
    if (mod && event.key.toLowerCase() === "z") { event.preventDefault(); void undo(); return; }
    if (mod) return;
    if (event.altKey && ev && !ev.allDay && /^Arrow/.test(event.key)) {
      event.preventDefault();
      if (!onUpdate) { setStatus(t.updateUnavailable); return; }
      const z = zoned.find(x => x.ev.id === ev.id)!;
      if (event.shiftKey && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
        if (z.endKey !== z.startKey) return;
        const end = Math.max(z.startMin + SNAP, Math.min(1440, z.endMin + (event.key === "ArrowDown" ? SNAP : -SNAP)));
        void runUpdate(moveTo(ev, z.startKey, z.startMin, end), ev, t.updated(ev.title));
      } else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        const delta = event.key === "ArrowDown" ? SNAP : -SNAP;
        const duration = ev.endDate.getTime() - ev.startDate.getTime();
        const next = { ...ev, startDate: new Date(ev.startDate.getTime() + delta * 60000), endDate: new Date(ev.startDate.getTime() + delta * 60000 + duration) };
        void runUpdate(next, ev, t.moved(ev.title, whenLabel(next)));
      } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        const days = event.key === "ArrowRight" ? 1 : -1;
        const s = zonedParts(ev.startDate, timeZone), e = zonedParts(ev.endDate, timeZone);
        const next = { ...ev, startDate: zonedInstant(...splitKey(addDaysKey(dayKey(s), days)), s.hour, s.minute, timeZone), endDate: zonedInstant(...splitKey(addDaysKey(dayKey(e), days)), e.hour, e.minute, timeZone) };
        void runUpdate(next, ev, t.moved(ev.title, whenLabel(next)));
      }
      return;
    }
    if (event.altKey) return;
    switch (event.key) {
      case "ArrowLeft": case "ArrowRight":
        if (view === "agenda") return;
        event.preventDefault();
        moveFocusDay(event.key === "ArrowRight" ? 1 : -1);
        return;
      case "ArrowUp": case "ArrowDown": {
        if (!ev) return;
        event.preventDefault();
        const at = ordered.findIndex(e => e.id === ev.id);
        const next = ordered[at + (event.key === "ArrowDown" ? 1 : -1)];
        if (next) root.current?.querySelector<HTMLElement>(`[data-tg-id="${CSS.escape(next.id)}"],[data-agenda-id="${CSS.escape(next.id)}"]`)?.focus();
        return;
      }
      case "Delete": case "Backspace": {
        const victim = ev ?? committed.find(e => e.id === selectedId);
        if (!victim) return;
        event.preventDefault();
        void runDelete(victim);
        return;
      }
      case "Escape":
        if (selectedId) { event.preventDefault(); setSelectedId(null); }
        return;
      case "Enter":
        if (!ev && target.dataset.day) { event.preventDefault(); const m = nextFreeHour(target.dataset.day); openCreate(target.dataset.day, m, m + 60); }
        return;
    }
    switch (event.key.toLowerCase()) {
      case "t": event.preventDefault(); goToday(); return;
      case "n": { event.preventDefault(); const key = range.includes(focusedDay) ? focusedDay : first; const m = nextFreeHour(key); openCreate(key, m, m + 60); return; }
      case "d": event.preventDefault(); setView("day"); return;
      case "w": event.preventDefault(); setView(narrow ? "3day" : "week"); return;
    }
  };

  // Scroll the day grid to the working morning once.
  useLayoutEffect(() => {
    const box = scroller.current;
    if (box && view !== "agenda") box.scrollTop = Math.max(0, (Math.max(startHour, 8) - startHour) * HOUR_PX - 8);
  }, [view]);

  const onSwipeStart = (e: ReactPointerEvent<HTMLDivElement>) => { if (e.pointerType !== "mouse" && narrow) swipe.current = { x: e.clientX, y: e.clientY, t: Date.now() }; };
  const onSwipeEnd = (e: ReactPointerEvent<HTMLDivElement>) => {
    const s = swipe.current;
    swipe.current = null;
    if (!s) return;
    const dx = e.clientX - s.x, dy = e.clientY - s.y;
    if (Math.abs(dx) > 60 && Math.abs(dy) < 40 && Date.now() - s.t < 700) shift(dx < 0 ? 1 : -1);
  };

  // ---- rendering ----
  const spring = reduced ? { duration: 0 } : motionTokens.spring.smooth;
  const slide = reduced
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: motionTokens.duration.instant } }
    : { initial: { x: `${direction * 30}%`, opacity: 0 }, animate: { x: 0, opacity: 1 }, exit: { x: `${-direction * 30}%`, opacity: 0 }, transition: spring };
  const canCreate = !!onCreate;
  const pendingIds = new Set(proposals.keys());

  const renderEvent = (seg: Segment, lane: { lane: number; lanes: number }, index: number) => {
    const ev = seg.ev;
    const movable = !!onUpdate && seg.first && seg.last && !pendingIds.has(ev.id);
    const style: CSSProperties = {
      top: timeGridPercent(seg.start, windowMinutes),
      height: timeGridPercent(Math.max(seg.end - seg.start, SNAP), windowMinutes),
      left: `calc(${(lane.lane / lane.lanes) * 100}% + 2px)`,
      right: `calc(${((lane.lanes - lane.lane - 1) / lane.lanes) * 100}% + 2px)`,
    };
    const MotionEvent = motion.button as unknown as "button";
    return <TimeGridEvent key={`${ev.id}-${index}`} as={MotionEvent} type="button" eventId={ev.id} startMinute={seg.start} endMinute={seg.end} movable={movable} accent={calendarColor(ev.calendarId)}
      className={styles.event} style={style} aria-label={eventLabel(ev)} aria-pressed={selectedId === ev.id} data-pending={pendingIds.has(ev.id) || undefined}
      {...({ layoutId: reduced ? undefined : `${uid}-${ev.id}-${seg.day}`, layout: reduced ? false : "position", transition: spring } as object)}
      onClick={(e: MouseEvent<HTMLButtonElement>) => openDetails(ev, e.currentTarget)}>
      {renderEventContent ? renderEventContent({ event: ev, calendar: calendars.find(c => c.id === ev.calendarId), day: seg.day, startLabel: timeFmt.format(ev.startDate), endLabel: timeFmt.format(ev.endDate), pending: pendingIds.has(ev.id), selected: selectedId === ev.id, first: seg.first, last: seg.last }) : <>
        <span className={styles.eventTitle}>{ev.title}</span>
        <span className={styles.eventTime}>{timeFmt.format(ev.startDate)} – {timeFmt.format(ev.endDate)}</span>
        {ev.location && <span className={styles.eventPlace}><MapPin size={11} aria-hidden="true" />{ev.location}</span>}
        {(ev.attendees?.length ?? 0) > 0 && <span className={styles.eventPeople}>{ev.attendees!.slice(0, 4).map(p => <Avatar key={p.name} name={p.name} src={p.avatar} size="sm" />)}</span>}
      </>}
    </TimeGridEvent>;
  };

  const columns: TimeGridColumn[] = range.map(key => {
    const segs = segments.get(key) ?? [];
    const lanes = placeOverlaps(segs.map((s, i) => ({ id: `${s.ev.id}#${i}`, start: s.start, end: s.end })));
    const isToday = key === todayKey;
    const date = keyAsDate(key);
    const count = segs.length + allDay.filter(z => diffDays(z.startKey, key) >= 0 && diffDays(key, z.endKey) >= 0).length;
    return {
      key,
      label: t.cellLabel(dateFmt.format(date), count),
      header: <div className={styles.dayHeader} data-today={isToday || undefined}>
        <span className={styles.dayName}>{shortFmt.format(date)}</span>
        <span className={styles.dayNumber} aria-current={isToday ? "date" : undefined}>{parseKey(key).day}</span>
      </div>,
      children: segs.map((s, i) => renderEvent(s, lanes.get(`${s.ev.id}#${i}`) ?? { lane: 0, lanes: 1 }, i)),
      cellProps: { tabIndex: focusedDay === key ? 0 : -1, "data-day": key, onFocus: (e: FocusEvent<HTMLDivElement>) => { if (e.target === e.currentTarget) setFocusedDay(key); } } as TimeGridColumn["cellProps"],
    };
  });
  const visibleAllDay = allDayOpen ? allDay : allDay.slice(0, ALLDAY_ROWS);
  const allDayContent = allDay.length > 0 ? <>
    {visibleAllDay.map(z => {
      const from = Math.max(0, diffDays(first, z.startKey)), to = Math.min(range.length - 1, diffDays(first, z.endKey));
      return <button key={z.ev.id} type="button" className={styles.allDayEvent} style={{ gridColumn: `${from + 1} / ${to + 2}`, "--tg-event-accent": calendarColor(z.ev.calendarId) } as CSSProperties}
        data-tg-id={z.ev.id} aria-label={eventLabel(z.ev)} data-pending={pendingIds.has(z.ev.id) || undefined} onClick={e => openDetails(z.ev, e.currentTarget)}>{z.ev.title}</button>;
    })}
    {allDay.length > ALLDAY_ROWS && <button type="button" className={styles.allDayMore} style={{ gridColumn: "1 / -1" }} aria-expanded={allDayOpen} onClick={() => setAllDayOpen(o => !o)}>{allDayOpen ? "−" : t.more(allDay.length - ALLDAY_ROWS)}</button>}
  </> : undefined;

  const nowMinute = nowParts.hour * 60 + nowParts.minute - windowStart;
  const viewOptions: CalendarView[] = narrow ? ["3day", "day", "agenda"] : ["week", "workweek", "day"];
  const toggles = calendars.map(c => <Checkbox key={c.id} checked={!hidden.has(c.id)} onCheckedChange={on => setHidden(cur => { const next = new Set(cur); if (on === true) next.delete(c.id); else next.add(c.id); return next; })}
    label={<span className={styles.toggleLabel}><span className={styles.swatch} style={{ background: colorOf(c.color) }} aria-hidden="true" />{c.name}</span>} />);

  const popupEvent = popup?.kind === "details" ? committed.find(e => e.id === popup.id) : undefined;

  return <div ref={root} className={[styles.root, className].filter(Boolean).join(" ")} style={style} data-narrow={narrow || undefined} data-density={compact ? "compact" : undefined} onKeyDown={onKeyDown}>
    {withSidebar && !narrow && <aside className={styles.sidebar}>
      <Calendar value={keyAsLocalDate(focusedDay)} month={miniMonth} onMonthChange={setMiniMonth} onChange={d => { const key = localDateKey(d); setDirection(diffDays(anchor, key) >= 0 ? 1 : -1); navigated(); setAnchor(key); setFocusedDay(key); }}
        weekStartsOn={weekStartsOn === 1 ? 1 : 0} locale={locale} today={keyAsLocalDate(todayKey)} className={styles.mini} />
      {calendars.length > 0 && <fieldset className={styles.toggles}><legend className={styles.togglesTitle}>{t.calendars}</legend>{toggles}</fieldset>}
    </aside>}
    <div className={styles.main}>
      <header className={styles.header}>
        <h2 className={styles.range} id={`${uid}-range`}>{rangeLabel}</h2>
        <div className={styles.nav}>
          <Button type="button" variant="secondary" size="sm" onClick={goToday} aria-keyshortcuts="T">{t.today}</Button>
          <button type="button" className={styles.iconButton} aria-label={t.previous} onClick={() => shift(-1)}><ChevronLeft size={16} aria-hidden="true" /></button>
          <button type="button" className={styles.iconButton} aria-label={t.next} onClick={() => shift(1)}><ChevronRight size={16} aria-hidden="true" /></button>
        </div>
        <div className={styles.tools}>
          <SegmentedControl label={t.views} value={view} options={viewOptions.map(v => ({ value: v, label: viewNames[v] }))} onValueChange={v => setView(v as CalendarView)} />
          {(!withSidebar || narrow) && calendars.length > 0 && <DropdownMenuRoot>
            <DropdownMenuTrigger asChild><button type="button" className={styles.iconButton} aria-label={t.calendars}><SlidersHorizontal size={16} aria-hidden="true" /></button></DropdownMenuTrigger>
            <DropdownMenuPortal><DropdownMenuContentBase className={dropdownMenuClassNames.menu} align="end" sideOffset={6} collisionPadding={12}>
              {calendars.map(c => <DropdownMenuCheckboxItem key={c.id} className={dropdownMenuClassNames.item} checked={!hidden.has(c.id)} onSelect={e => e.preventDefault()}
                onCheckedChange={on => setHidden(cur => { const next = new Set(cur); if (on) next.delete(c.id); else next.add(c.id); return next; })}>
                <span className={styles.toggleLabel}><span className={styles.swatch} style={{ background: colorOf(c.color) }} aria-hidden="true" />{c.name}</span>
                <DropdownMenuItemIndicator className={styles.menuCheck}><Check size={14} aria-hidden="true" /></DropdownMenuItemIndicator>
              </DropdownMenuCheckboxItem>)}
            </DropdownMenuContentBase></DropdownMenuPortal>
          </DropdownMenuRoot>}
          <Button type="button" variant="primary" size="sm" disabled={!canCreate} aria-label={t.newEvent} aria-keyshortcuts="N" onClick={event => { const key = range.includes(focusedDay) ? focusedDay : first; const m = nextFreeHour(key); openCreate(key, m, m + 60, event.currentTarget); }}><Plus size={16} aria-hidden="true" /></Button>
        </div>
      </header>
      <p id={`${uid}-help`} className={styles.srOnly}>{t.instructions}</p>
      <div ref={scroller} className={styles.scroller} onPointerDown={onSwipeStart} onPointerUp={onSwipeEnd} onPointerCancel={() => { swipe.current = null; }}>
        <LayoutGroup id={uid}>
          <AnimatePresence mode="popLayout" initial={false}>
            <PresenceDiv key={navKey} className={styles.slide} {...slide}>
              {view === "agenda" ? <ol className={styles.agenda} aria-labelledby={`${uid}-range`}>
                {range.map(key => {
                  const items = zoned.filter(z => diffDays(z.startKey, key) >= 0 && diffDays(key, z.endKey) >= 0).sort((a, b) => Number(!!b.ev.allDay) - Number(!!a.ev.allDay) || a.ev.startDate.getTime() - b.ev.startDate.getTime()).map(z => z.ev);
                  return <li key={key} className={styles.agendaDay}>
                    <h3 className={styles.agendaDate} data-today={key === todayKey || undefined}>{dateFmt.format(keyAsDate(key))}</h3>
                    {items.length === 0 ? <p className={styles.agendaEmpty}>{t.noEvents}</p> : <ul className={styles.agendaList}>
                      {items.map(ev => <li key={ev.id}><button type="button" className={styles.agendaRow} data-agenda-id={ev.id} data-tg-id={ev.id} aria-label={eventLabel(ev)} style={{ "--tg-event-accent": calendarColor(ev.calendarId) } as CSSProperties} onClick={e => openDetails(ev, e.currentTarget)}>
                        <span className={styles.agendaTime}>{ev.allDay ? t.allDay : timeFmt.format(new Date(Math.max(ev.startDate.getTime(), instantAt(key, 0).getTime())))}</span><span className={styles.agendaTitle}>{ev.title}</span>{ev.location && <span className={styles.agendaPlace}>{ev.location}</span>}
                      </button></li>)}
                    </ul>}
                  </li>;
                })}
              </ol> : <TimeGrid
                className={styles.grid}
                classNames={{ head: styles.gridHead, allDay: styles.gridAllDay }}
                aria-label={t.gridLabel(rangeLabel)}
                aria-describedby={`${uid}-help`}
                columns={columns}
                graduations={Array.from({ length: endHour - startHour }, (_, i) => ({ key: startHour + i, label: i === 0 ? "" : hourFmt.format(new Date(Date.UTC(2000, 0, 1, startHour + i))) }))}
                windowMinutes={windowMinutes}
                snapMinutes={SNAP}
                escapeCancels
                selectDescription={t.instructions}
                onSelectRange={canCreate ? (r: TimeGridRange) => openCreate(r.columnKey, r.startMinute + windowStart, r.endMinute + windowStart) : undefined}
                onEventChange={onUpdate ? onEventChange : undefined}
                now={range.includes(todayKey) && nowMinute >= 0 && nowMinute <= windowMinutes ? { minute: nowMinute, columnKeys: [todayKey], label: timeFmt.format(now), labelColumnKey: first } : null}
                allDay={allDayContent ? { label: t.allDay, content: allDayContent } : null}
              />}
            </PresenceDiv>
          </AnimatePresence>
        </LayoutGroup>
      </div>
      <AnimatePresence>
        {toast && <PresenceDiv key={toast.message + (toast.undo ? "u" : "")} className={styles.toast} role="status" initial={reduced ? { opacity: 0 } : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={spring}>
          <span>{toast.message}</span>
          {toast.undo && <button type="button" className={styles.toastUndo} onClick={() => void undo(toast.undo)}>{t.undo}</button>}
          <button type="button" className={styles.toastClose} aria-label="Dismiss" onClick={() => setToast(null)}>×</button>
        </PresenceDiv>}
      </AnimatePresence>
    </div>
    <Popover open={!!popup} onOpenChange={open => { if (!open) closePopup(); }}>
      <PopoverAnchor asChild><span className={styles.anchor} style={popup ? { top: popup.rect.top, left: popup.rect.left, width: popup.rect.width, height: popup.rect.height } : undefined} aria-hidden="true" /></PopoverAnchor>
      {popup && <PopoverContent side={narrow ? "bottom" : "right"} align="start" className={styles.popover} aria-label={popup.kind === "create" ? t.newEvent : popupEvent?.title ?? t.newEvent} role="dialog"
        onCloseAutoFocus={e => e.preventDefault()}>
        {popup.kind === "create" ? <form className={styles.popForm} aria-busy={busy || undefined} onSubmit={e => { e.preventDefault(); void saveDraft(); }}>
          <Input autoFocus aria-label={t.title} placeholder={t.untitled} value={draftTitle} disabled={busy} onChange={e => setDraftTitle(e.target.value)} />
          <p className={styles.popWhen}>{dateFmt.format(keyAsDate(popup.day))} · {timeFmt.format(instantAt(popup.day, popup.start))} – {timeFmt.format(popup.end >= 1440 ? instantAt(addDaysKey(popup.day, 1), popup.end - 1440) : instantAt(popup.day, popup.end))}</p>
          {calendars.length > 0 && <Select aria-label={t.calendar} value={draftCalendar} onValueChange={setDraftCalendar} options={calendars.map(c => ({ value: c.id, label: c.name }))} />}
          {formError && <p className={styles.popError} role="alert">{formError}</p>}
          {!canCreate && <p className={styles.popNote}>{t.createUnavailable}</p>}
          <div className={styles.popActions}>
            <Button type="button" variant="ghost" size="sm" onClick={closePopup}>{t.cancel}</Button>
            <Button type="submit" variant="primary" size="sm" loading={busy} disabled={!canCreate || busy}>{t.save}</Button>
          </div>
        </form> : popupEvent ? <form className={styles.popForm} aria-busy={busy || undefined} onSubmit={e => { e.preventDefault(); void saveDetails(); }}>
          <Input aria-label={t.title} value={form.title} disabled={busy || !onUpdate} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          {!popupEvent.allDay && <div className={styles.popRow}>
            <Input type="date" aria-label={t.date} value={form.date} disabled={busy || !onUpdate} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            <Input type="time" step={SNAP * 60} aria-label={t.start} value={form.start} disabled={busy || !onUpdate} onChange={e => setForm(f => ({ ...f, start: e.target.value }))} />
            <Input type="time" step={SNAP * 60} aria-label={t.end} value={form.end} disabled={busy || !onUpdate} onChange={e => setForm(f => ({ ...f, end: e.target.value }))} />
          </div>}
          {calendars.length > 0 && <Select aria-label={t.calendar} value={form.calendarId} disabled={busy || !onUpdate} onValueChange={v => setForm(f => ({ ...f, calendarId: v }))} options={calendars.map(c => ({ value: c.id, label: c.name }))} />}
          <Input aria-label={t.location} placeholder={t.location} value={form.location} disabled={busy || !onUpdate} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
          {formError && <p className={styles.popError} role="alert">{formError}</p>}
          {!onUpdate && <p className={styles.popNote}>{t.updateUnavailable}</p>}
          <div className={styles.popActions}>
            <Button type="button" variant="danger" size="sm" disabled={!onDelete || busy} onClick={() => void deleteFromDetails()}>{t.delete}</Button>
            <span className={styles.popSpacer} />
            <Button type="button" variant="ghost" size="sm" onClick={closePopup}>{t.cancel}</Button>
            <Button type="submit" variant="primary" size="sm" loading={busy} disabled={!onUpdate || busy}>{t.save}</Button>
          </div>
        </form> : null}
      </PopoverContent>}
    </Popover>
    <LiveStatus>{status}</LiveStatus>
  </div>;
}

function splitKey(key: string): [number, number, number] { const { year, month, day } = parseKey(key); return [year, month, day]; }

export default WeekCalendar;
